import { useState } from 'react'

import { pick } from '@eastcoal/common/utils/object'

import { useCrudFormStore } from '@eastcoal/formily/components/CrudForm/Store/hooks/useStore'
import { SubmitContextType } from '@eastcoal/formily/components/CrudForm/Store/store/interface'

import { FeeMethod } from '@eastcoal/shared/enums/FeeMethod'

interface FeeFieldConfig {
  key: 'serviceFeeMethod' | 'entranceFeeMethod'
  label: string
}

/**
 * 需要二次确认的费用字段配置
 */
const FEE_FIELDS: FeeFieldConfig[] = [
  { key: 'serviceFeeMethod', label: '服务费' },
  { key: 'entranceFeeMethod', label: '入场费' }
]

interface UseFeeConfirmOptions {
  /**
   * 提交成功后的回调（如 keepon 模式下重置表单）
   */
  onSubmitted?: () => void
}

interface UseFeeConfirmResult {
  /**
   * 弹层是否打开
   */
  isModalOpen: boolean
  /**
   * 弹层提示内容
   */
  confirmContent: string
  /**
   * 点击按钮时触发（含表单预校验）
   */
  handleClick: (context?: SubmitContextType) => Promise<void>
  /**
   * 弹层确认
   */
  handleConfirm: () => void
  /**
   * 弹层取消
   */
  handleCancel: () => void
}

/**
 * 竞价场次费用设置二次确认逻辑
 *
 * 当服务费或入场费为"不收取"时，先弹层确认再继续提交
 */
export const useFeeConfirm = ({
  onSubmitted
}: UseFeeConfirmOptions = {}): UseFeeConfirmResult => {
  const { form, submit } = useCrudFormStore(state =>
    pick(state, ['form', 'submit'])
  )

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [confirmContent, setConfirmContent] = useState('')
  const [pendingContext, setPendingContext] = useState<
    SubmitContextType | undefined
  >()

  /**
   * 根据字段值构建确认提示文案
   */
  const buildConfirmContent = (values: Record<string, unknown>) => {
    const emptyLabels = FEE_FIELDS.filter(
      field => values[field.key] === FeeMethod.NONE
    ).map(field => field.label)

    if (emptyLabels.length === 0) {
      return ''
    }

    return `是否确认本场次不收取${emptyLabels.join('、')}？`
  }

  /**
   * 执行提交，成功后触发回调
   */
  const doSubmit = async (context?: SubmitContextType) => {
    await submit(context)
    onSubmitted?.()
  }

  const handleClick = async (context?: SubmitContextType) => {
    const content = buildConfirmContent(form.values)

    // 无需提醒，直接提交（submit 内部会先校验）
    if (!content) {
      doSubmit(context)
      return
    }

    // 需要二次确认：先校验表单，不通过则停留在表单页展示错误，不弹费用确认
    // formily 校验失败会 throw（字段错误已渲染），捕获后直接返回
    try {
      await form.validate()
    } catch {
      return
    }

    // 打开弹层并暂存提交上下文
    setConfirmContent(content)
    setPendingContext(context)
    setIsModalOpen(true)
  }

  const handleConfirm = () => {
    setIsModalOpen(false)
    if (pendingContext) {
      doSubmit(pendingContext)
    }
    setPendingContext(undefined)
  }

  const handleCancel = () => {
    setIsModalOpen(false)
    setPendingContext(undefined)
  }

  return {
    isModalOpen,
    confirmContent,
    handleClick,
    handleConfirm,
    handleCancel
  }
}
